import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Plus, Minus, ChefHat, GlassWater, Trash2, ShoppingCart, Users, StickyNote, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, getDocs, addDoc, doc, updateDoc, serverTimestamp, query, where } from 'firebase/firestore';
import toast from 'react-hot-toast';

/**
 * Normaliza los modifiers del estado local antes de persistir en Firestore.
 *
 * El estado local guarda cada extra como `{ id, name, price }` (donde `id` es
 * el menuItemId del extra). Para que el trigger de stockDeduction encuentre la
 * receta del extra y descuente el inventario, el campo en Firestore debe
 * llamarse `menuItemId`, no `id`.
 *
 * Mantenemos `name` y `price` para display y cálculos. `id` se renombra a
 * `menuItemId`. Si el modifier ya viene con `menuItemId` (compat con orígenes
 * que ya lo emiten así), se preserva.
 */
const normalizeModifiersForFirestore = (modifiers) => {
  if (!Array.isArray(modifiers)) return [];
  return modifiers
    .filter(Boolean)
    .map((m) => ({
      menuItemId: m.menuItemId || m.id || null,
      name: m.name,
      price: typeof m.price === 'number' ? m.price : 0,
    }));
};

const ExpressOrderModal = ({ isOpen, onClose }) => {
  const { restaurantId } = useAuth();
  
  // Estados
  const [step, setStep] = useState(1); // 1: Seleccionar mesa, 2: Agregar items, 3: Confirmar
  const [tables, setTables] = useState([]);
  const [zones, setZones] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [guestCount, setGuestCount] = useState(1);
  
  // Estados para venta sin mesa
  const [isNoTableOrder, setIsNoTableOrder] = useState(false);
  const [customerName, setCustomerName] = useState('');
  
  // Estado para sesión express existente (agregar items a orden activa)
  const [existingSession, setExistingSession] = useState(null);
  const [expressSessionsMap, setExpressSessionsMap] = useState({});

  // Estado para item con notas/extras
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemNotes, setItemNotes] = useState('');
  const [itemExtras, setItemExtras] = useState([]);

  // Cargar mesas y menú al abrir
  useEffect(() => {
    if (isOpen && restaurantId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, restaurantId]);

  // Reset cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedTable(null);
      setOrderItems([]);
      setSearchTerm('');
      setSelectedCategory('all');
      setGuestCount(1);
      setIsNoTableOrder(false);
      setCustomerName('');
      setExistingSession(null);
      setExpressSessionsMap({});
      setEditingItemId(null);
      setItemNotes('');
      setItemExtras([]);
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoadingData(true);
    try {
      // Cargar mesas activas (disponibles + ocupadas con express)
      const tablesRef = collection(db, 'restaurants', restaurantId, 'tables');
      const tablesSnapshot = await getDocs(tablesRef);
      const allTables = tablesSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(table => table.isActive !== false);

      // Cargar sesiones express activas para detectar mesas con orden abierta
      const sessionsRef = collection(db, 'restaurants', restaurantId, 'tableSessions');
      const expressQuery = query(
        sessionsRef,
        where('isExpressOrder', '==', true),
        where('status', '==', 'active')
      );
      const expressSnapshot = await getDocs(expressQuery);
      const sessionsMap = {};
      expressSnapshot.docs.forEach(d => {
        const data = d.data();
        if (data.tableId) {
          sessionsMap[data.tableId] = { id: d.id, ...data };
        }
      });
      setExpressSessionsMap(sessionsMap);

      // Filtrar: mesas disponibles + mesas ocupadas con sesión express activa
      const tablesData = allTables.filter(table => 
        table.status === 'available' || 
        (table.status === 'occupied' && sessionsMap[table.id])
      );
      setTables(tablesData);

      // Cargar zonas
      const zonesRef = collection(db, 'restaurants', restaurantId, 'zones');
      const zonesSnapshot = await getDocs(zonesRef);
      const zonesData = zonesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setZones(zonesData);

      // Cargar categorías
      const categoriesRef = collection(db, 'restaurants', restaurantId, 'menuCategories');
      const categoriesSnapshot = await getDocs(categoriesRef);
      const categoriesData = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMenuCategories(categoriesData);

      // Cargar items del menú
      const itemsRef = collection(db, 'restaurants', restaurantId, 'menuItems');
      const itemsSnapshot = await getDocs(itemsRef);
      const itemsData = itemsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(item => item.isActive !== false);
      setMenuItems(itemsData);

    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoadingData(false);
    }
  };

  const getZoneName = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    return zone?.name || null;
  };

  // Filtrar items
  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.categoryId === selectedCategory;
    const isNotExtra = item.type !== 'extra'; // No mostrar extras como items principales
    return matchesSearch && matchesCategory && isNotExtra;
  });

  // Obtener extras disponibles para un item
  const getAvailableExtras = (item) => {
    if (!item.availableExtras || item.availableExtras.length === 0) return [];
    return menuItems.filter(mi => item.availableExtras.includes(mi.id) && mi.type === 'extra');
  };

  // Agregar item a la orden
  const addItemToOrder = (menuItem) => {
    const existingIndex = orderItems.findIndex(oi => 
      oi.menuItemId === menuItem.id && 
      !oi.specialRequests && 
      (!oi.modifiers || oi.modifiers.length === 0)
    );

    if (existingIndex >= 0) {
      const updated = [...orderItems];
      updated[existingIndex].quantity += 1;
      setOrderItems(updated);
    } else {
      const price = typeof menuItem.price === 'object' ? menuItem.price.amount : menuItem.price;
      setOrderItems([...orderItems, {
        id: `temp_${Date.now()}_${Math.random()}`,
        menuItemId: menuItem.id,
        name: menuItem.name,
        price: price || 0,
        quantity: 1,
        type: menuItem.type,
        specialRequests: '',
        modifiers: [],
        availableExtras: menuItem.availableExtras || []
      }]);
    }
  };

  // Actualizar cantidad
  const updateItemQuantity = (itemId, delta) => {
    const updated = orderItems.map(item => {
      if (item.id === itemId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean);
    setOrderItems(updated);
  };

  // Eliminar item
  const removeItem = (itemId) => {
    setOrderItems(orderItems.filter(item => item.id !== itemId));
    if (editingItemId === itemId) {
      setEditingItemId(null);
      setItemNotes('');
      setItemExtras([]);
    }
  };

  // Abrir editor de notas/extras
  const openItemEditor = (item) => {
    setEditingItemId(item.id);
    setItemNotes(item.specialRequests || '');
    setItemExtras(item.modifiers || []);
  };

  // Guardar notas/extras
  const saveItemDetails = () => {
    const updated = orderItems.map(item => {
      if (item.id === editingItemId) {
        return {
          ...item,
          specialRequests: itemNotes,
          modifiers: itemExtras
        };
      }
      return item;
    });
    setOrderItems(updated);
    setEditingItemId(null);
    setItemNotes('');
    setItemExtras([]);
  };

  // Toggle extra
  const toggleExtra = (extra) => {
    const exists = itemExtras.find(e => e.id === extra.id);
    if (exists) {
      setItemExtras(itemExtras.filter(e => e.id !== extra.id));
    } else {
      const price = typeof extra.price === 'object' ? extra.price.amount : extra.price;
      setItemExtras([...itemExtras, {
        id: extra.id,
        name: extra.name,
        price: price || 0
      }]);
    }
  };

  // Calcular total
  const calculateTotal = () => {
    return orderItems.reduce((total, item) => {
      const itemTotal = item.price * item.quantity;
      const extrasTotal = (item.modifiers || []).reduce((sum, mod) => sum + (mod.price || 0), 0) * item.quantity;
      return total + itemTotal + extrasTotal;
    }, 0);
  };

  // Crear sesión y órdenes (o agregar a sesión existente)
  const submitOrder = async () => {
    if (orderItems.length === 0) return;
    if (!isNoTableOrder && !selectedTable) return;

    setLoading(true);
    try {
      const orderItemsRef = collection(db, 'restaurants', restaurantId, 'orderItems');
      const newItemsTotal = calculateTotal();

      if (existingSession) {
        // --- AGREGAR ITEMS A SESIÓN EXPRESS EXISTENTE ---
        const sessionId = existingSession.id;
        const orderName = selectedTable.label || selectedTable.name || `Mesa ${selectedTable.number}`;

        for (const item of orderItems) {
          await addDoc(orderItemsRef, {
            sessionId,
            tableId: selectedTable.id,
            tableName: orderName,
            menuItemId: item.menuItemId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            totalPrice: (item.price + (item.modifiers || []).reduce((sum, m) => sum + (m.price || 0), 0)) * item.quantity,
            type: item.type,
            status: 'queued',
            specialRequests: item.specialRequests || null,
            notes: item.specialRequests || null,
            // Persistir cada modifier con `menuItemId` (id del menuItem del extra)
            // para que el trigger de stockDeduction descuente su receta del inventario.
            modifiers: normalizeModifiersForFirestore(item.modifiers),
            isExpressOrder: true,
            isNoTableOrder: false,
            orderedByName: 'Orden Express',
            orderedByGuestId: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }

        const prevSubtotal = existingSession.financials?.subtotal || 0;
        const prevTotal = existingSession.financials?.total || 0;
        const sessionRef = doc(db, 'restaurants', restaurantId, 'tableSessions', sessionId);
        await updateDoc(sessionRef, {
          'financials.subtotal': prevSubtotal + newItemsTotal,
          'financials.total': prevTotal + newItemsTotal,
          guestCount: guestCount,
          updatedAt: serverTimestamp()
        });

        toast.success(`Items agregados a la orden de ${orderName}`);
      } else {
        // --- CREAR NUEVA SESIÓN EXPRESS ---
        const sessionsRef = collection(db, 'restaurants', restaurantId, 'tableSessions');
        const orderName = isNoTableOrder 
          ? (customerName.trim() || 'Venta Rápida')
          : (selectedTable.label || selectedTable.name || `Mesa ${selectedTable.number}`);
        
        const sessionData = {
          tableId: isNoTableOrder ? null : selectedTable.id,
          tableName: orderName,
          status: 'active',
          guestCount: isNoTableOrder ? 1 : guestCount,
          isExpressOrder: true,
          isNoTableOrder: isNoTableOrder,
          customerName: isNoTableOrder ? (customerName.trim() || null) : null,
          openedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          billingMode: 'table',
          financials: {
            subtotal: newItemsTotal,
            total: newItemsTotal,
            currency: 'CLP'
          }
        };

        const sessionDoc = await addDoc(sessionsRef, sessionData);
        const sessionId = sessionDoc.id;

        if (!isNoTableOrder && selectedTable) {
          const tableRef = doc(db, 'restaurants', restaurantId, 'tables', selectedTable.id);
          await updateDoc(tableRef, {
            status: 'occupied',
            currentSessionId: sessionId,
            updatedAt: serverTimestamp()
          });
        }

        for (const item of orderItems) {
          await addDoc(orderItemsRef, {
            sessionId,
            tableId: isNoTableOrder ? null : selectedTable.id,
            tableName: orderName,
            menuItemId: item.menuItemId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            totalPrice: (item.price + (item.modifiers || []).reduce((sum, m) => sum + (m.price || 0), 0)) * item.quantity,
            type: item.type,
            status: 'queued',
            specialRequests: item.specialRequests || null,
            notes: item.specialRequests || null,
            // Misma normalización que arriba — extras quedan con `menuItemId`
            // para que el inventario los descuente correctamente al ready.
            modifiers: normalizeModifiersForFirestore(item.modifiers),
            isExpressOrder: true,
            isNoTableOrder: isNoTableOrder,
            orderedByName: isNoTableOrder ? (customerName.trim() || 'Venta Rápida') : 'Orden Express',
            orderedByGuestId: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }

        // Touch session to trigger onSnapshot reload after all items exist
        const sessionRef = doc(db, 'restaurants', restaurantId, 'tableSessions', sessionId);
        await updateDoc(sessionRef, { updatedAt: serverTimestamp() });

        const successMessage = isNoTableOrder 
          ? `Venta Rápida creada${customerName.trim() ? ` para ${customerName.trim()}` : ''}`
          : `Orden Express creada para ${selectedTable.label || 'Mesa ' + selectedTable.number}`;
        toast.success(successMessage);
      }

      onClose();
    } catch (error) {
      console.error('Error procesando orden:', error);
      toast.error(existingSession ? 'Error al agregar items a la orden' : 'Error al crear la orden');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-2 md:p-4">
      <div className="bg-surface-1 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-line flex flex-col">
        
        {/* Header */}
        <div className="p-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-kumia-green/20 flex items-center justify-center">
              <ShoppingCart className="text-kumia-green" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-content-primary">Orden Express</h2>
              <p className="text-content-muted text-sm">
                {step === 1 && 'Selecciona una opción'}
                {step === 2 && (isNoTableOrder 
                  ? `Venta Rápida${customerName.trim() ? ` • ${customerName.trim()}` : ''} • Agrega los items`
                  : `${selectedTable?.label || 'Mesa ' + selectedTable?.number} • ${existingSession ? 'Agregar items a orden existente' : 'Agrega los items'}`
                )}
                {step === 3 && 'Confirma la orden'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-4 rounded-lg transition-colors"
          >
            <X className="text-content-muted" size={20} />
          </button>
        </div>

        {/* Steps indicator */}
        <div className="px-4 py-3 border-b border-line flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= s ? 'bg-kumia-green text-black' : 'bg-surface-4 text-content-subtle'
              }`}>
                {step > s ? <Check size={16} /> : s}
              </div>
              <span className={`text-sm ${step >= s ? 'text-content-primary' : 'text-content-subtle'}`}>
                {s === 1 && 'Mesa'}
                {s === 2 && 'Items'}
                {s === 3 && 'Confirmar'}
              </span>
              {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-kumia-green' : 'bg-surface-4'}`} />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingData ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kumia-green"></div>
            </div>
          ) : (
            <>
              {/* Step 1: Seleccionar Mesa o Venta Rápida */}
              {step === 1 && (
                <div>
                  {/* Opción de Venta Rápida (Sin Mesa) */}
                  <div className="mb-6">
                    <button
                      onClick={() => {
                        setIsNoTableOrder(true);
                        setSelectedTable(null);
                        setExistingSession(null);
                      }}
                      className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                        isNoTableOrder
                          ? 'border-kumia-green bg-kumia-green/10'
                          : 'border-line-strong bg-surface-2 hover:border-line-active'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <ShoppingCart className="text-purple-700 dark:text-purple-400" size={24} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className={`font-bold text-lg ${isNoTableOrder ? 'text-kumia-green' : 'text-content-primary'}`}>
                          Venta Rápida (Sin Mesa)
                        </div>
                        <div className="text-sm text-content-muted">
                          Para clientes que no usan mesa ni la app
                        </div>
                      </div>
                    </button>
                    
                    {/* Campo de nombre cuando es venta sin mesa */}
                    {isNoTableOrder && (
                      <div className="mt-3 p-4 bg-surface-2 rounded-lg border border-line-strong">
                        <label className="block text-sm font-medium text-content-secondary mb-2">
                          Nombre del cliente (opcional)
                        </label>
                        <input
                          type="text"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder="Ej: Juan, Cliente mostrador..."
                          className="w-full bg-surface-4 border border-line-strong rounded-lg px-4 py-2 text-content-primary placeholder-content-subtle focus:border-kumia-green focus:outline-none"
                        />
                      </div>
                    )}
                  </div>

                  {/* Separador */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex-1 h-px bg-surface-5"></div>
                    <span className="text-content-subtle text-sm">o selecciona una mesa</span>
                    <div className="flex-1 h-px bg-surface-5"></div>
                  </div>

                  {/* Número de comensales (solo para mesa) */}
                  {!isNoTableOrder && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-content-secondary mb-2">
                        {existingSession ? 'Comensales actuales' : 'Número de comensales'}
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                          className="w-10 h-10 rounded-lg bg-surface-4 hover:bg-surface-5 flex items-center justify-center text-content-primary"
                        >
                          <Minus size={16} />
                        </button>
                        <div className="w-16 h-10 rounded-lg bg-surface-2 border border-line-strong flex items-center justify-center">
                          <span className="text-content-primary font-bold">{guestCount}</span>
                        </div>
                        <button
                          onClick={() => setGuestCount(guestCount + 1)}
                          className="w-10 h-10 rounded-lg bg-surface-4 hover:bg-surface-5 flex items-center justify-center text-content-primary"
                        >
                          <Plus size={16} />
                        </button>
                        <Users className="text-content-muted ml-2" size={20} />
                      </div>
                    </div>
                  )}

                  <h3 className="text-lg font-semibold text-content-primary mb-3">Mesas Disponibles / Con Orden Express Activa</h3>
                  
                  {tables.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-content-muted">No hay mesas disponibles en este momento</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {tables.map((table) => {
                        const hasActiveExpress = !!expressSessionsMap[table.id];
                        return (
                          <button
                            key={table.id}
                            onClick={() => {
                              setSelectedTable(table);
                              const session = hasActiveExpress ? expressSessionsMap[table.id] : null;
                              setExistingSession(session);
                              if (session) setGuestCount(session.guestCount || 1);
                              setIsNoTableOrder(false);
                            }}
                            className={`p-4 rounded-xl border-2 transition-all relative ${
                              selectedTable?.id === table.id && !isNoTableOrder
                                ? 'border-kumia-green bg-kumia-green/10'
                                : hasActiveExpress
                                  ? 'border-amber-500/50 bg-amber-500/5 hover:border-amber-500/80'
                                  : 'border-line-strong bg-surface-2 hover:border-line-active'
                            }`}
                          >
                            {hasActiveExpress && (
                              <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-500 text-black text-xs font-bold rounded-full">
                                Orden activa
                              </div>
                            )}
                            <div className={`font-bold ${selectedTable?.id === table.id && !isNoTableOrder ? 'text-kumia-green' : 'text-content-primary'}`}>
                              {table.label || `Mesa ${table.number}`}
                            </div>
                            {(table.zoneId || table.zone) && (
                              <div className="text-xs text-content-subtle">
                                {getZoneName(table.zoneId || table.zone)}
                              </div>
                            )}
                            <div className="text-xs text-content-muted">
                              Cap: {table.capacity || '?'} personas
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Agregar Items */}
              {step === 2 && (
                <div className="flex gap-4 h-full">
                  {/* Lista de items del menú */}
                  <div className="flex-1">
                    {/* Search y filtros */}
                    <div className="flex gap-2 mb-4">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" size={18} />
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Buscar item..."
                          className="w-full bg-surface-2 border border-line-strong rounded-lg pl-10 pr-4 py-2 text-content-primary placeholder-content-subtle focus:border-kumia-green focus:outline-none"
                        />
                      </div>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="bg-surface-2 border border-line-strong rounded-lg px-3 py-2 text-content-primary focus:border-kumia-green focus:outline-none"
                      >
                        <option value="all">Todas las categorías</option>
                        {menuCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Items grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-2">
                      {filteredItems.map((item) => {
                        const price = typeof item.price === 'object' ? item.price.amount : item.price;
                        return (
                          <button
                            key={item.id}
                            onClick={() => addItemToOrder(item)}
                            className="flex items-center gap-3 p-3 bg-surface-2 hover:bg-surface-4 border border-line-strong rounded-lg text-left transition-colors"
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              item.type === 'food' ? 'bg-orange-500/20' : 'bg-blue-500/20'
                            }`}>
                              {item.type === 'food' ? (
                                <ChefHat className="text-orange-700 dark:text-orange-400" size={18} />
                              ) : (
                                <GlassWater className="text-blue-700 dark:text-blue-400" size={18} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-content-primary font-medium truncate">{item.name}</div>
                              <div className="text-kumia-green text-sm font-bold">${price?.toFixed(2) || '0.00'}</div>
                            </div>
                            <Plus className="text-content-muted" size={18} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Orden actual */}
                  <div className="w-80 bg-surface-2 rounded-xl border border-line-strong flex flex-col">
                    <div className="p-3 border-b border-line-strong">
                      <h4 className="text-content-primary font-semibold flex items-center gap-2">
                        <ShoppingCart size={16} />
                        Orden Actual ({orderItems.length})
                      </h4>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                      {orderItems.length === 0 ? (
                        <div className="text-center py-8 text-content-subtle text-sm">
                          Agrega items del menú
                        </div>
                      ) : (
                        orderItems.map((item) => (
                          <div
                            key={item.id}
                            className={`p-3 rounded-lg border ${
                              editingItemId === item.id 
                                ? 'border-kumia-green bg-kumia-green/5' 
                                : 'border-line-strong bg-surface-4'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="text-content-primary font-medium text-sm truncate">{item.name}</div>
                                <div className="text-kumia-green text-xs">${item.price?.toFixed(2)}</div>
                                {item.modifiers?.length > 0 && (
                                  <div className="text-purple-700 dark:text-purple-400 text-xs mt-1">
                                    +{item.modifiers.length} extra(s)
                                  </div>
                                )}
                                {item.specialRequests && (
                                  <div className="text-yellow-700 dark:text-yellow-400 text-xs mt-1 truncate">
                                    📝 {item.specialRequests}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => updateItemQuantity(item.id, -1)}
                                  className="w-6 h-6 rounded bg-surface-5 hover:bg-surface-5 flex items-center justify-center"
                                >
                                  <Minus size={12} className="text-content-primary" />
                                </button>
                                <span className="w-6 text-center text-content-primary text-sm">{item.quantity}</span>
                                <button
                                  onClick={() => updateItemQuantity(item.id, 1)}
                                  className="w-6 h-6 rounded bg-surface-5 hover:bg-surface-5 flex items-center justify-center"
                                >
                                  <Plus size={12} className="text-content-primary" />
                                </button>
                              </div>
                            </div>
                            
                            <div className="flex gap-1 mt-2">
                              <button
                                onClick={() => openItemEditor(item)}
                                className="flex-1 text-xs py-1 px-2 rounded bg-surface-5 hover:bg-surface-5 text-content-secondary flex items-center justify-center gap-1"
                              >
                                <StickyNote size={12} />
                                Notas/Extras
                              </button>
                              <button
                                onClick={() => removeItem(item.id)}
                                className="text-xs py-1 px-2 rounded bg-red-500/20 hover:bg-red-500/30 text-red-700 dark:text-red-400"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>

                            {/* Editor de notas/extras */}
                            {editingItemId === item.id && (
                              <div className="mt-3 pt-3 border-t border-line-strong space-y-3">
                                <div>
                                  <label className="block text-xs text-content-muted mb-1">Notas especiales</label>
                                  <textarea
                                    value={itemNotes}
                                    onChange={(e) => setItemNotes(e.target.value)}
                                    placeholder="Ej: Sin cebolla, poco picante..."
                                    className="w-full bg-surface-2 border border-line-strong rounded-lg p-2 text-content-primary text-xs placeholder-content-subtle focus:border-kumia-green focus:outline-none resize-none"
                                    rows={2}
                                  />
                                </div>
                                
                                {item.availableExtras?.length > 0 && (
                                  <div>
                                    <label className="block text-xs text-content-muted mb-1">Extras disponibles</label>
                                    <div className="space-y-1">
                                      {getAvailableExtras({ availableExtras: item.availableExtras }).map(extra => {
                                        const extraPrice = typeof extra.price === 'object' ? extra.price.amount : extra.price;
                                        const isSelected = itemExtras.some(e => e.id === extra.id);
                                        return (
                                          <button
                                            key={extra.id}
                                            onClick={() => toggleExtra(extra)}
                                            className={`w-full flex items-center justify-between p-2 rounded text-xs ${
                                              isSelected 
                                                ? 'bg-purple-500/20 border border-purple-500' 
                                                : 'bg-surface-2 border border-line-strong hover:border-line-active'
                                            }`}
                                          >
                                            <span className={isSelected ? 'text-purple-700 dark:text-purple-400' : 'text-content-secondary'}>
                                              {extra.name}
                                            </span>
                                            <span className={isSelected ? 'text-purple-700 dark:text-purple-400' : 'text-content-muted'}>
                                              +${extraPrice?.toFixed(2)}
                                            </span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                <button
                                  onClick={saveItemDetails}
                                  className="w-full py-2 bg-kumia-green hover:bg-green-600 text-black rounded-lg text-xs font-bold transition-colors"
                                >
                                  Guardar cambios
                                </button>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    {/* Total */}
                    <div className="p-3 border-t border-line-strong">
                      <div className="flex justify-between items-center">
                        <span className="text-content-muted">Total</span>
                        <span className="text-xl font-bold text-kumia-green">${calculateTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Confirmar */}
              {step === 3 && (
                <div className="max-w-lg mx-auto">
                  <div className="bg-surface-2 rounded-xl border border-line-strong p-6">
                    <h3 className="text-lg font-bold text-content-primary mb-4">
                      {existingSession ? 'Items a Agregar' : 'Resumen de la Orden'}
                    </h3>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between py-2 border-b border-line-strong">
                        <span className="text-content-muted">{isNoTableOrder ? 'Tipo' : 'Mesa'}</span>
                        <span className="text-content-primary font-medium">
                          {isNoTableOrder 
                            ? (customerName.trim() || 'Venta Rápida')
                            : (selectedTable?.label || `Mesa ${selectedTable?.number}`)
                          }
                        </span>
                      </div>
                      {isNoTableOrder && (
                        <div className="flex justify-between py-2 border-b border-line-strong">
                          <span className="text-content-muted">Modalidad</span>
                          <span className="text-purple-700 dark:text-purple-400 font-medium">Sin Mesa</span>
                        </div>
                      )}
                      {!isNoTableOrder && (
                        <div className="flex justify-between py-2 border-b border-line-strong">
                          <span className="text-content-muted">Comensales</span>
                          <span className="text-content-primary font-medium">{guestCount}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 border-b border-line-strong">
                        <span className="text-content-muted">Items</span>
                        <span className="text-content-primary font-medium">{orderItems.reduce((sum, i) => sum + i.quantity, 0)}</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                      {orderItems.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <div className="flex-1">
                            <span className="text-content-primary">{item.name}</span>
                            <span className="text-content-subtle"> x{item.quantity}</span>
                            {item.modifiers?.length > 0 && (
                              <div className="text-purple-700 dark:text-purple-400 text-xs">
                                + {item.modifiers.map(m => m.name).join(', ')}
                              </div>
                            )}
                            {item.specialRequests && (
                              <div className="text-yellow-700 dark:text-yellow-400 text-xs">📝 {item.specialRequests}</div>
                            )}
                          </div>
                          <span className="text-content-muted">
                            ${((item.price + (item.modifiers || []).reduce((sum, m) => sum + m.price, 0)) * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {existingSession && (
                      <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-amber-700 dark:text-amber-400">Total actual de la orden</span>
                          <span className="text-amber-700 dark:text-amber-400 font-bold">${(existingSession.financials?.total || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-content-muted">Nuevos items</span>
                          <span className="text-content-primary font-bold">+${calculateTotal().toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-4 border-t border-line-strong">
                      <span className="text-lg font-bold text-content-primary">
                        {existingSession ? 'Nuevo Total' : 'Total'}
                      </span>
                      <span className="text-2xl font-bold text-kumia-green">
                        ${existingSession 
                          ? ((existingSession.financials?.total || 0) + calculateTotal()).toFixed(2)
                          : calculateTotal().toFixed(2)
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-line flex justify-between">
          <button
            onClick={() => {
              if (step > 1) setStep(step - 1);
              else onClose();
            }}
            className="px-6 py-2 bg-surface-4 hover:bg-surface-5 text-content-primary rounded-lg font-medium transition-colors"
          >
            {step === 1 ? 'Cancelar' : 'Atrás'}
          </button>

          <button
            onClick={() => {
              if (step === 1 && (selectedTable || isNoTableOrder)) setStep(2);
              else if (step === 2 && orderItems.length > 0) setStep(3);
              else if (step === 3) submitOrder();
            }}
            disabled={
              (step === 1 && !selectedTable && !isNoTableOrder) ||
              (step === 2 && orderItems.length === 0) ||
              loading
            }
            className="px-6 py-2 bg-kumia-green hover:bg-green-600 text-black rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent"></div>
                Procesando...
              </>
            ) : (
              <>
                {step === 3 
                  ? (existingSession ? 'Agregar Items' : (isNoTableOrder ? 'Crear Venta Rápida' : 'Crear Orden Express')) 
                  : 'Continuar'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExpressOrderModal;
